import React, { useState } from 'react';
import { PerksContainer } from '../PerksContainer';
import { Coupon as CouponType } from '../types';
import { Coupon } from '../Coupon';
import { styles } from './CouponsContainer.css';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import {
  BIRTHDAY_MESSAGE,
  DEFAULT_MESSAGE,
  DEFAULT_CONTAINER_TITLE,
  TITLE_MINI,
  TITLE_ATTACH_SLIDEOUT,
} from './constants';

type CouponsContainerProps = {
  variant?: 'mini' | 'full';
  coupons?: Array<CouponType>;
  title?: typeof DEFAULT_CONTAINER_TITLE | typeof TITLE_MINI | typeof TITLE_ATTACH_SLIDEOUT;
  shouldShowSubheading?: boolean;
  isCloseToBirthMonth?: boolean;
  shouldDisplayStatus?: boolean;
  viewLimit?: number;
  onApplyCoupon: (couponNumber: string) => void;
  onRemoveCoupon?: (couponNumber: string) => void;
};

export const CouponsContainer = ({
  variant = 'full',
  coupons,
  title,
  shouldShowSubheading = false,
  isCloseToBirthMonth = false,
  shouldDisplayStatus = true,
  viewLimit = 2,
  onApplyCoupon,
  onRemoveCoupon,
}: CouponsContainerProps) => {
  const [shouldShowMoreCoupons, setShouldShowMoreCoupons] = useState(false);

  const calculatedViewLimit = Math.max(viewLimit, coupons?.filter(coupon => coupon.isApplied)?.length ?? 0);

  const content = (
    <div className={styles.couponsContainer({ variant })}>
      {title ? (
        <CalloutFont className={styles.header} as="h3">
          {title}
        </CalloutFont>
      ) : null}
      {coupons?.length ? (
        <>
          {shouldShowSubheading ? <p>You can only use one coupon per order</p> : null}
          {coupons
            .sort((a, b) => (b.isApplied ? 1 : 0) - (a.isApplied ? 1 : 0))
            .slice(0, shouldShowMoreCoupons ? coupons.length : calculatedViewLimit)
            .map((coupon, index) => (
              <Coupon
                key={`${coupon.couponName}-${index}`}
                viewMode={variant}
                type="perks"
                name={coupon.content?.displayName || coupon.couponName}
                number={coupon.couponCode}
                expiry={coupon.expiryDate}
                isApplied={coupon.isApplied}
                isBestDeal={coupon.isBestDeal}
                success={shouldDisplayStatus ? coupon.success : true}
                onApplyCoupon={onApplyCoupon}
                onRemoveCoupon={onRemoveCoupon}
              />
            ))}
        </>
      ) : (
        <p>{isCloseToBirthMonth ? BIRTHDAY_MESSAGE : DEFAULT_MESSAGE}</p>
      )}
      {!shouldShowMoreCoupons && (coupons?.length ?? 0) > calculatedViewLimit ? (
        <Button
          type="linkWithIcon"
          label={'Show more coupons'}
          icon={<Icon name="Arrow" />}
          onClick={() => setShouldShowMoreCoupons(true)}
        />
      ) : null}

      {shouldShowMoreCoupons ? (
        <Button
          type="linkWithIcon"
          label={'Show less coupons'}
          icon={<Icon name="Arrow" className={styles.showLessIcon} />}
          onClick={() => setShouldShowMoreCoupons(false)}
        />
      ) : null}
    </div>
  );

  return variant === 'full' ? <PerksContainer padding="small">{content}</PerksContainer> : content;
};
